import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';

/** ข้อมูลเทมเพลตที่ตั้งค่าไว้ใน APP-BO (หน้า booking-template/templates) */
export interface TemplateThemeIF {
  logoFile: string | null;
  coverFile: string | null;
  isCalendar: boolean;
  themeKey: string | null;
  isDark: boolean;
  primaryColor: string;
  name: string;
}

/** ชุดสีที่ใช้กับหน้าจอ */
export interface ThemePaletteIF {
  /** สีหลัก */
  primary: string;
  /** สีรอง (ใช้ทำ gradient คู่กับสีหลัก) */
  secondary: string;
  /** สีตัด (ทอง/เน้น) */
  accent: string;
  /** สีพื้นอ่อน */
  tint: string;
}

/** ผสมสี hex สองสีตามสัดส่วน weight (0 = a, 1 = b) */
export function mixHex(a: string, b: string, weight: number): string {
  const pa = parseHex(a), pb = parseHex(b);
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * weight));
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
}

function parseHex(hex: string): number[] {
  let h = (hex || '#000000').replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const n = parseInt(h.substring(0, 6), 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** สร้างชุดสีจากสีหลักที่ตั้งค่าไว้ (สูตรเดียวกับ customPalette ของ APP-BO) */
export function paletteFromPrimary(primary: string): ThemePaletteIF {
  return {
    primary,
    secondary: mixHex(primary, '#FFFFFF', 0.35),
    accent: '#D4A646',
    tint: mixHex(primary, '#FFFFFF', 0.94),
  };
}

/**
 * ทำให้สีสว่างขึ้นอย่างน้อยถึงระดับ minLightness (0-1) แต่คงเฉดและความสดของสีไว้
 * (ใช้กับตัวอักษร/ไอคอนบนพื้นมืด ไม่ให้ซีดเป็นสีเทาเหมือนการผสมสีขาว)
 */
export function lightenKeepHue(hex: string, minLightness: number): string {
  const [r, g, b] = parseHex(hex).map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (l >= minLightness) return hex;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  h *= 60;
  // HSL -> RGB ที่ความสว่างใหม่
  const c = (1 - Math.abs(2 * minLightness - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = minLightness - c / 2;
  const [r1, g1, b1] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] :
    h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return '#' + [r1, g1, b1].map((v) => Math.round((v + m) * 255).toString(16).padStart(2, '0')).join('');
}

/** สีพื้นการ์ดในโหมดมืด (ต้องตรงกับ --theme-surface ใน theme-dark.scss) */
const DARK_SURFACE = '#131c2e';

/**
 * ไล่เฉดสี 50-950 โดยให้สีฐานอยู่ที่เฉด 600 (ระบบใช้ blue-600 เป็นสีหลัก)
 * โหมดมืด: เฉดอ่อน 50-200 (ใช้เป็นพื้นหลัง/ขอบ) จะเป็นโทนมืดอมสีแทน
 */
function shadeScale(base: string, isDark = false): Record<number, string> {
  return {
    50: isDark ? mixHex(base, DARK_SURFACE, 0.82) : mixHex(base, '#FFFFFF', 0.92),
    100: isDark ? mixHex(base, DARK_SURFACE, 0.72) : mixHex(base, '#FFFFFF', 0.84),
    200: isDark ? mixHex(base, DARK_SURFACE, 0.55) : mixHex(base, '#FFFFFF', 0.68),
    300: mixHex(base, '#FFFFFF', 0.5),
    400: mixHex(base, '#FFFFFF', 0.28),
    500: mixHex(base, '#FFFFFF', 0.12),
    600: base,
    700: mixHex(base, '#000000', 0.15),
    800: mixHex(base, '#000000', 0.3),
    900: mixHex(base, '#000000', 0.45),
    950: mixHex(base, '#000000', 0.6),
  };
}

const CACHE_KEY = 'templateTheme';

/** สี Tailwind ที่หน้าจอใช้เป็นสีรอง (gradient คู่สีหลัก, กล่องข้อมูลผู้เข้าพัก ฯลฯ) */
const SECONDARY_COLORS = ['indigo', 'violet', 'purple', 'cyan'];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private http = inject(HttpClient);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private _loadedAppId: string | null = null;

  /** ข้อมูลเทมเพลตล่าสุด */
  readonly template = signal<TemplateThemeIF | null>(null);

  /** ชื่อ / โลโก้ / ภาพปก ที่ตั้งค่าไว้ (null = ไม่ได้ตั้ง) */
  readonly name = computed(() => this.template()?.name || null);
  readonly logoUrl = computed(() => this.fileUrl(this.template()?.logoFile));
  readonly coverUrl = computed(() => this.fileUrl(this.template()?.coverFile));

  /**
   * ดึงสีเทมเพลตของร้านแล้วนำไปใช้ทั้งระบบ
   * เรียกครั้งเดียวต่อ appId
   */
  loadTheme(appId: string | null | undefined) {
    if (!this.isBrowser || !appId || this._loadedAppId === appId) return;
    this._loadedAppId = appId;

    // ใช้ค่าที่เคยโหลดไว้ก่อน เพื่อไม่ให้หน้าจอกระพริบเป็นสีเดิม
    const cached = this.readCache(appId);
    if (cached) this.apply(cached);

    this.http
      .get<{ data: TemplateThemeIF }>(`/api/Public/Setting/Template/Theme`, { headers: { 'www-app-id': appId } })
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (!res?.data) return;
        this.apply(res.data);
        this.writeCache(appId, res.data);
      });
  }

  private apply(t: TemplateThemeIF) {
    this.template.set(t);
    // ใช้สีหลักที่ตั้งค่าไว้ (primaryColor จาก API)
    const palette = paletteFromPrimary(t.primaryColor || '#2563EB');
    const html = document.documentElement;
    const root = html.style;

    // แทนที่สี Tailwind ที่หน้าจอใช้เป็นสีหลัก (blue) และสีรอง (SECONDARY_COLORS)
    const primary = shadeScale(palette.primary, t.isDark);
    const secondary = shadeScale(palette.secondary, t.isDark);
    for (const [shade, color] of Object.entries(primary)) {
      root.setProperty(`--color-blue-${shade}`, color);
      root.setProperty(`--p-primary-${shade}`, color); // PrimeNG
    }
    for (const [shade, color] of Object.entries(secondary)) {
      for (const name of SECONDARY_COLORS) root.setProperty(`--color-${name}-${shade}`, color);
    }

    root.setProperty('--theme-primary', palette.primary);
    root.setProperty('--theme-secondary', palette.secondary);
    root.setProperty('--theme-accent', palette.accent);
    root.setProperty('--theme-tint', palette.tint);

    // ใช้ใน theme-dark.scss: ตัวอักษรสีหลัก/สีรองบนพื้นมืด และตัวอักษรเฉดอ่อนบนพื้น gradient
    for (const [group, base] of [['primary', palette.primary], ['secondary', palette.secondary]]) {
      root.setProperty(`--theme-${group}-text`, lightenKeepHue(base, 0.68));
      const light = shadeScale(base, false);
      for (const shade of [100, 200, 300]) root.setProperty(`--theme-${group}-light-${shade}`, light[shade]);
    }

    html.classList.toggle('theme-dark', !!t.isDark);
    if (t.name) document.title = t.name;
  }

  private fileUrl(file: string | null | undefined) {
    return file ? environment.imgURL + file : null;
  }

  private readCache(appId: string): TemplateThemeIF | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      const c = raw ? JSON.parse(raw) : null;
      return c?.appId === appId ? c.data : null;
    } catch {
      return null;
    }
  }

  private writeCache(appId: string, data: TemplateThemeIF) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ appId, data }));
    } catch { }
  }
}
